import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MessageCircle, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  { icon: <Mail size={18} />, label: "Email Us", value: "hello@emiratechdis.com", href: "mailto:hello@emiratechdis.com" },
  { icon: <Phone size={18} />, label: "Call Us", value: "+971 58 592 7936", href: "tel:+971585927936" },
  { icon: <MessageCircle size={18} />, label: "Telegram", value: "@emiratech", href: "https://t.me/emiratech" },
  { icon: <MapPin size={18} />, label: "Visit Us", value: "Dubai, UAE", href: null },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus("error");
      setErrorMsg("Please fill in name, email and message.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
      setTimeout(() => setStatus("idle"), 5000);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Failed to send. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="snap-section" style={{ background: "linear-gradient(180deg, #05101f 0%, #071528 100%)" }}>
      <div className="max-w-[1280px] mx-auto px-6 w-full">
        <div className="max-w-3xl mb-8">
          <div className="section-eyebrow">Contact</div>
          <h2 className="section-title" data-testid="contact-title">Get In Touch</h2>
          <p className="section-subtitle" data-testid="contact-subtitle">
            Ready to elevate your technology infrastructure? Contact us today.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-1 gap-3">
            {contactInfo.map((c, i) => (
              <a
                key={c.label}
                data-testid={`contact-info-${i}`}
                href={c.href || undefined}
                target={c.href?.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="glass-card p-4 flex items-start gap-3 no-underline"
              >
                <div className="svc-icon" style={{ width: 38, height: 38 }}>{c.icon}</div>
                <div className="min-w-0">
                  <div className="text-[#7fa5e6] text-[11px] uppercase tracking-wider mb-0.5" style={{ fontFamily: "Sora, sans-serif" }}>{c.label}</div>
                  <div className="text-white text-[13.5px] font-medium break-all">{c.value}</div>
                </div>
              </a>
            ))}
          </div>

          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="lg:col-span-3 glass-card p-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[#a6b3c8] text-xs mb-1.5 block uppercase tracking-wider" style={{ fontFamily: "Sora, sans-serif" }}>Your Name *</label>
                <input
                  data-testid="contact-input-name"
                  name="name"
                  className="input-field"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={onChange}
                />
              </div>
              <div>
                <label className="text-[#a6b3c8] text-xs mb-1.5 block uppercase tracking-wider" style={{ fontFamily: "Sora, sans-serif" }}>Email Address *</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  name="email"
                  className="input-field"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-[#a6b3c8] text-xs mb-1.5 block uppercase tracking-wider" style={{ fontFamily: "Sora, sans-serif" }}>Company Name</label>
              <input
                data-testid="contact-input-company"
                name="company"
                className="input-field"
                placeholder="Your organization"
                value={form.company}
                onChange={onChange}
              />
            </div>

            <div className="mt-4">
              <label className="text-[#a6b3c8] text-xs mb-1.5 block uppercase tracking-wider" style={{ fontFamily: "Sora, sans-serif" }}>Message *</label>
              <textarea
                data-testid="contact-input-message"
                name="message"
                className="input-field"
                placeholder="Tell us about your project or requirements..."
                value={form.message}
                onChange={onChange}
                rows={3}
                style={{ minHeight: 90 }}
              />
            </div>

            {status === "error" && (
              <div data-testid="contact-error" className="mt-3 text-[#ff7c76] text-sm">{errorMsg}</div>
            )}

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={status === "loading"}
              className="btn-primary mt-5 w-full sm:w-auto justify-center disabled:opacity-70"
              style={{ padding: "12px 24px", fontSize: 14 }}
            >
              {status === "loading" ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : status === "success" ? (
                <>
                  <CheckCircle2 size={18} /> Message Sent!
                </>
              ) : (
                <>
                  Send Message <Send size={16} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
